import { ArrowLeft, Delete, KeyRound, Loader2, Mail } from "lucide-react";

// ----------------------------------------------------------------------------
// "Forgot admin PIN" panel: code entry → new-pin entry (two phases).
// Mirrors the OTP screen's visual language so operators can pattern-match.
// ----------------------------------------------------------------------------
export default function ResetPanel({ resetStep, press, loading, onBack, onSubmit, setResetStep }) {
  const isCodePhase = resetStep.phase === "code";
  return (
    <div data-testid="reset-screen">
      <button
        type="button"
        onClick={onBack}
        className="text-xs text-stone-500 hover:text-stone-800 inline-flex items-center gap-1 mb-4"
        data-testid="reset-back"
      >
        <ArrowLeft className="h-3 w-3" /> Back to PIN
      </button>
      <div className="flex justify-center mb-4">
        <div className="h-14 w-14 rounded-full bg-amber-100 flex items-center justify-center">
          {isCodePhase ? (
            <Mail className="h-7 w-7 text-amber-800" />
          ) : (
            <KeyRound className="h-7 w-7 text-amber-800" />
          )}
        </div>
      </div>
      <div className="text-center mb-6">
        <div className="font-display text-2xl font-bold tracking-tight">
          {isCodePhase ? "Enter reset code" : "Set new PIN"}
        </div>
        <div className="text-stone-600 mt-1.5 text-sm">
          {resetStep.sending ? (
            <span className="inline-flex items-center gap-1.5">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Sending code…
            </span>
          ) : isCodePhase ? (
            <>
              We sent a 6-digit code to{" "}
              <span
                className="font-semibold text-stone-800"
                data-testid="reset-recipient"
              >
                {resetStep.sentTo}
              </span>
              .
            </>
          ) : (
            <>Type the new PIN (4+ digits), then re-enter to confirm.</>
          )}
        </div>
      </div>

      {isCodePhase ? (
        <div className="flex justify-center gap-2 mb-6" data-testid="reset-code-display">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className={`h-12 w-9 rounded-md border-2 flex items-center justify-center font-display font-bold text-xl ${
                resetStep.code.length > i
                  ? "border-amber-500 bg-amber-50 text-amber-900"
                  : "border-stone-300 text-stone-300"
              }`}
            >
              {resetStep.code[i] || ""}
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 mb-6" data-testid="reset-pin-display">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-1.5">New PIN</div>
            <div className="flex gap-1.5">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className={`h-10 w-7 rounded-md border-2 flex items-center justify-center font-display font-bold ${
                    resetStep.newPin.length > i
                      ? "border-amber-500 bg-amber-50 text-amber-900"
                      : "border-stone-300 text-stone-300"
                  }`}
                >
                  {resetStep.newPin[i] ? "•" : ""}
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-1.5">Confirm</div>
            <div className="flex gap-1.5">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className={`h-10 w-7 rounded-md border-2 flex items-center justify-center font-display font-bold ${
                    resetStep.confirmPin.length > i
                      ? "border-emerald-500 bg-emerald-50 text-emerald-900"
                      : "border-stone-300 text-stone-300"
                  }`}
                >
                  {resetStep.confirmPin[i] ? "•" : ""}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3" data-testid="reset-numpad">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
          <button
            key={n}
            onClick={() => press(String(n))}
            disabled={loading || resetStep.sending}
            data-testid={`reset-key-${n}`}
            className="h-16 rounded-xl bg-white border border-border text-2xl font-display font-bold hover:bg-stone-50 active:scale-95 transition shadow-sm"
          >
            {n}
          </button>
        ))}
        <button
          onClick={() =>
            setResetStep((r) =>
              r
                ? r.phase === "code"
                  ? { ...r, code: "" }
                  : { ...r, newPin: "", confirmPin: "" }
                : r
            )
          }
          className="h-16 rounded-xl bg-stone-100 hover:bg-stone-200 text-sm font-semibold uppercase tracking-wider"
          data-testid="reset-clear"
        >
          Clear
        </button>
        <button
          onClick={() => press("0")}
          disabled={loading || resetStep.sending}
          data-testid="reset-key-0"
          className="h-16 rounded-xl bg-white border border-border text-2xl font-display font-bold hover:bg-stone-50 active:scale-95 transition shadow-sm"
        >
          0
        </button>
        <button
          onClick={() =>
            setResetStep((r) => {
              if (!r) return r;
              if (r.phase === "code") return { ...r, code: r.code.slice(0, -1) };
              if (r.confirmPin) return { ...r, confirmPin: r.confirmPin.slice(0, -1) };
              return { ...r, newPin: r.newPin.slice(0, -1) };
            })
          }
          data-testid="reset-backspace"
          className="h-16 rounded-xl bg-stone-100 hover:bg-stone-200 flex items-center justify-center"
        >
          <Delete className="h-6 w-6" />
        </button>
      </div>

      <button
        onClick={onSubmit}
        disabled={
          loading ||
          (isCodePhase
            ? resetStep.code.length < 6
            : resetStep.newPin.length < 4 || resetStep.confirmPin.length < 4)
        }
        data-testid="reset-submit"
        className="mt-6 w-full h-14 rounded-xl bg-primary text-white font-semibold text-lg disabled:opacity-50 hover:bg-primary/90 active:scale-[0.99] transition flex items-center justify-center gap-2"
      >
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : isCodePhase ? (
          "Continue"
        ) : (
          "Set new PIN"
        )}
      </button>
    </div>
  );
}
